import { Controller } from "@hotwired/stimulus";
import { patch, post, destroy as del } from "@rails/request.js";
import { createSelect2Manager } from "../lib/select2_manager";
import { showConfirmModal, showAlertModal } from "../lib/custom_confirm";
import { formatDeliveryDaysSummary } from "../lib/delivery_days_summary";
import { deliveryWindowError, timeInputValue } from "../lib/delivery_window";
import { setHidden } from "../lib/display_toggle";
import { syncGateReason } from "../lib/gate_reason";
import { SAVE_UNCHANGED_REASON } from "../lib/form_fingerprint";
import { clearButtonBusy, setButtonLoading } from "../lib/button_loading";
import {
  integrationCardFor,
  isIntegrationCardConnected,
  setIntegrationCardCollapsed,
  setIntegrationCardState,
} from "../lib/integration_card_state";
import { statusBadgeClass } from '../lib/status_badge';

const LICENSED_STATE_SELECT_ALL_EXCLUSIONS = new Set(["NY"]);

// Shown when the test lead was delivered to a URL that is NOT the saved webhook:
// the server refuses to enable the saved one from that result (#6705).
const UNSAVED_WEBHOOK_TESTED_MESSAGE =
  'Test lead delivered, but this URL is not your saved webhook — save it, then test again to enable delivery.';

// BUTTON-9: why an integration Save that HAS changes is still disabled.
const WEBHOOK_URL_REASON = 'Enter a URL starting with http:// or https://';
const INSURDIAL_KEY_REASON = 'Enter an InsurDial API key';

// BUTTON-4: why an integration action is disabled. The view stamps the same
// strings on first paint (UsersHelper::INTEGRATION_GATE_REASONS).
const WEBHOOK_NOT_SAVED_REASON = 'Save a webhook URL first';
const INSURDIAL_NOT_SAVED_REASON = 'No InsurDial API key is saved yet';
const SHARE_EMAIL_REASON = 'Enter a valid email address first';

// A saved webhook that is not connected yet says why in its card body; a passed
// Test connects the card, and its header pill takes over (#6705).
const WEBHOOK_STATUS_BADGES = {
  failed: { label: 'Test Failed', tone: 'danger' },
  not_tested: { label: 'Not Tested', tone: 'warning' },
};

export default class extends Controller {
  static targets = [
    "licensedStates",
    "sameAsHome",
    "homeAddressStreet",
    "homeAddressCity",
    "homeAddressState",
    "homeAddressZip",
    "homeAddressCountry",
    "businessAddressStreet",
    "businessAddressCity",
    "businessAddressState",
    "businessAddressZip",
    "businessAddressCountry",
    "businessAddressGroup",
    "businessAddressSummary",
    "businessAddressSummaryText",
    "defaultsSameAsProfile",
    "useProfileInfoForThankYouPage",
    "firstName",
    "lastName",
    "npnId",
    "emailAddress",
    "phone",
    "website",
    "displayName",
    "preferredFirstName",
    "displayTitle",
    "displayNpn",
    "displayEmail",
    "displayPhone",
    "displayCountryCode",
    "displayAgentWebsite",
    "displayPhotoPreview",
    "displayPhotoBorrowNote",
    "displayPhotoBorrowFlag",
    "displayTitleSource",
    "displayTitleSourceText",
    "useProfileTitle",
    "agentWebsite",
    "emailNotifications",
    "emailNotificationInterval",
    "smsNotifications",
    "smsNotificationInterval",
    "ringyContainer",
    "ringyTemplate",
    "ringyCard",
    "webhookCardContainer",
    "webhookUrl",
    "insurdialCardContainer",
    "insurdialApiKey",
    "googleSheetContainer",
    "googleSheetTemplate",
    "googleSheetForm",
    "googleSheetFormContent",
    "googleSheetLoading",
    "googleSheetFooter",
    "googleSheetModalClose",
    "modalTitle",
    "modalFieldsTable",
    "statesCounter",
    "statesSummary",
    "daysSummary",
  ];

  static values = {
    ringyFields: Object,
    profileFirstName: String,
    profilePreferredFirstName: String,
    profileLastName: String,
    profileTitle: String,
    profileNpn: String,
    profileEmail: String,
    profilePhone: String,
    profileWebsite: String,
    // Variant URL of the PROFILE photo (blank when there is none). Only ever
    // painted into the Display Photo preview as the borrow the server will
    // make on save (Users::FillDisplayPhotoFromProfile) — never submitted.
    profilePhotoUrl: String,
  };

  // Hooks the Lead Defaults Display Photo slot exposes (#12911). The slot's
  // own file input is never disabled by "Use profile info": an agent can
  // always choose a different picture (owner ruling 5).
  static DISPLAY_PHOTO_INPUT_SELECTOR = "#user_display_photo";

  connect() {
    // Licensed-states is a static multi-select; on phones fall back to the
    // native control, which (unlike Select2) opens on tap on iOS.
    this.select2Manager = createSelect2Manager({ nativeOnTouch: true });
    // Store bound function references for cleanup
    this.boundHandlers = {};
    // Tracks whether the user has explicitly toggled the thank-you-page
    // checkbox in this session. If true, the auto-check logic in
    // handleSubmit leaves it alone (asymmetric: respect explicit intent).
    this.thankYouUserInteracted = false;
    this.initializeLicensedStatesSelect();
    this.setDefaultNotificationValues();
    this.setupEventListeners();
    this.setupFormValidation();
    this.updateStatesCounter();
    this.detectPersistentThankYouOptOut();
    this.syncTitleSource();
  }

  // If the page loads with every thank-you display field already populated
  // AND the thank-you checkbox unchecked, the user has previously saved
  // with this exact configuration and deliberately opted out — auto-check
  // would silently override that on the next save. Promote the in-memory
  // interaction flag so handleSubmit treats it as already-explicit.
  detectPersistentThankYouOptOut() {
    const thankYouCheckbox = this.element.querySelector(
      "#use_profile_info_for_thank_you_page",
    );
    if (!thankYouCheckbox || thankYouCheckbox.checked) return;
    if (this.allThankYouDisplayFieldsFilled()) {
      this.thankYouUserInteracted = true;
    }
  }

  allThankYouDisplayFieldsFilled() {
    const requiredDisplayFields = [
      "#user_display_title",
      "#user_display_name",
      "#user_display_npn",
      "#user_display_email",
      "#user_display_phone",
    ];
    return requiredDisplayFields.every((selector) => {
      const field = this.element.querySelector(selector);
      return field && field.value.trim() !== "";
    });
  }

  // Stimulus callback when sameAsHome target connects - handles initial page load
  sameAsHomeTargetConnected(element) {
    this.setBusinessAddressDisabled(element.checked);
    this.toggleBusinessAddressVisibility(element.checked);
    this.updateBusinessAddressSummary();
  }

  disconnect() {
    this.select2Manager?.destroyAll();
    this.select2Manager = null;
    this.cleanupEventListeners();
  }

  cleanupEventListeners() {
    // Clean up GHL test button
    const ghlTestBtn = document.getElementById('ghl-test-lead');
    if (ghlTestBtn && this.boundHandlers.handleTestGhl) {
      ghlTestBtn.removeEventListener('click', this.boundHandlers.handleTestGhl);
    }

    // Clear all references
    this.boundHandlers = {};
  }

  initializeLicensedStatesSelect() {
    if (!this.hasLicensedStatesTarget) return;

    this.select2Manager.init(
      this.licensedStatesTarget,
      () => ({
        placeholder: "Select states",
        allowClear: true,
        multiple: true,
        width: "100%",
      }),
      {
        onInitialized: (element, $element) => {
          // select2 fires only a jQuery `change`; native listeners (card-dirty's
          // dirty detection, Stimulus `change->` actions) never hear it, so the
          // card's Save button stayed disabled when states changed. Bridge it to a
          // native bubbling change. The `bridging` guard prevents the re-entrant
          // jQuery handler (which also fires on the native event) from looping, and
          // means programmatic `.trigger("change")` from All/None bridges too.
          let bridging = false;
          const handler = () => {
            this.updateStatesCounter();
            if (bridging) return;
            bridging = true;
            try {
              element.dispatchEvent(new Event("change", { bubbles: true }));
            } finally {
              bridging = false;
            }
          };
          $element.on("change", handler);
          return () => $element.off("change", handler);
        },
      },
    );

    // Select2 hides the native <select> and renders its own .select2-container.
    // Propagate server-rendered is-invalid so the red outline appears on the
    // widget.
    if (this.licensedStatesTarget.classList.contains("is-invalid")) {
      const container = this.licensedStatesTarget.nextElementSibling;
      if (container?.classList.contains("select2-container")) {
        container.classList.add("is-invalid");
      }
    }
  }

  setDefaultNotificationValues() {
    // Send Agent Lead Details On Lead Assignment - default to checked
    const sendEmailCheckbox = document.getElementById("user_send_email");
    const sendTextCheckbox = document.getElementById("user_send_text");

    // Only set defaults if the checkboxes exist and are not already checked
    if (sendEmailCheckbox && !sendEmailCheckbox.checked) {
      sendEmailCheckbox.checked = true;
    }

    if (sendTextCheckbox && !sendTextCheckbox.checked) {
      sendTextCheckbox.checked = true;
    }
  }

  setupEventListeners() {
    if (this.hasSameAsHomeTarget) {
      this.syncBusinessAddressIfNeeded();
    }
    if (this.hasDefaultsSameAsProfileTarget) {
      this.syncDefaultsIfNeeded();
    }
    this.toggleNotificationIntervals();
    this.setupWebhookButtons();
    this.setupInsurdialButtons();
    this.setupGhlButtons();
    this.syncIntegrationCardSaves();
  }

  // BUTTON-4 / BUTTON-9 / A11Y-1: a disabled button (Save, Delete, Test,
  // Invite …) says why — the reason as its gl-tooltip plus a
  // "<label> — <reason>" accessible name (lib/gate_reason) — and an enabled
  // one drops both. `reason` null means the button is enabled.
  applyGate(button, reason) {
    if (!button) return;
    syncGateReason(button, { ready: !reason, reason });
  }

  // The Ringy and Google Sheet card Saves render disabled server-side; give
  // each one its reason (and its real dirty state) once the controller connects.
  syncIntegrationCardSaves() {
    this.element.querySelectorAll('.ringy-integration-card').forEach((card) => this.syncRingySave(card));
    this.element.querySelectorAll('.google-sheet-card').forEach((card) => this.syncGoogleSheetSave(card));
  }

  setupWebhookButtons() {
    // Note: Click handlers are now attached via Stimulus data-action attributes in the view
    // Only set initial state of webhook buttons here
    this.toggleWebhookButtons();
  }

  toggleWebhookButtons() {
    const deleteWebhookBtn = document.getElementById('delete-webhook-btn');
    const testWebhookBtn = document.getElementById('webhook-test-lead');
    const saveWebhookBtn = document.getElementById('save-webhook-btn');
    const webhookUrlInput = document.getElementById('user_webhook_url');

    if (!webhookUrlInput) return;

    // Clear validation state when user edits the URL
    webhookUrlInput.classList.remove('is-invalid');

    const urlValue = webhookUrlInput.value.trim();
    const hasWebhookUrl = urlValue.length > 0;
    const hasValidPrefix = /^https?:\/\//i.test(urlValue);
    const initialValue = webhookUrlInput.dataset.initialValue || '';
    const hasSavedWebhook = initialValue.length > 0;
    const urlDirty = urlValue !== initialValue;

    // Checkbox is only enabled when a webhook has been saved
    const checkbox = document.getElementById('webhook_for_all_lead_orders');
    let checkboxDirty = false;
    if (checkbox) {
      checkbox.disabled = !hasSavedWebhook;
      if (!hasSavedWebhook && checkbox.checked) {
        checkbox.checked = false;
      }
      const initialChecked = checkbox.dataset.initialChecked === 'true';
      checkboxDirty = checkbox.checked !== initialChecked;
    }

    const isDirty = urlDirty || checkboxDirty;

    const notSavedReason = hasSavedWebhook ? null : WEBHOOK_NOT_SAVED_REASON;
    this.applyGate(deleteWebhookBtn, notSavedReason);
    this.applyGate(testWebhookBtn, notSavedReason);
    if (saveWebhookBtn) {
      const reason = !isDirty ? SAVE_UNCHANGED_REASON : (hasValidPrefix ? null : WEBHOOK_URL_REASON);
      this.applyGate(saveWebhookBtn, reason);
    }
  }

  setupGhlButtons() {
    const ghlTestBtn = document.getElementById('ghl-test-lead');
    if (ghlTestBtn) {
      this.boundHandlers.handleTestGhl = this.handleTestGhl.bind(this);
      ghlTestBtn.addEventListener('click', this.boundHandlers.handleTestGhl);
    }
  }

  async handleSaveWebhook(event) {
    event.preventDefault();
    const webhookUrlInput = document.getElementById('user_webhook_url');
    const webhookUrl = webhookUrlInput?.value?.trim();

    if (!webhookUrl) {
      await showAlertModal('Please enter a webhook URL.', { title: 'Invalid URL', variant: 'danger' });
      webhookUrlInput.classList.add('is-invalid');
      webhookUrlInput.focus();
      return;
    }

    if (!this.isValidUrl(webhookUrl)) {
      await showAlertModal('Please enter a valid URL starting with http:// or https://', { title: 'Invalid URL', variant: 'danger' });
      webhookUrlInput.classList.add('is-invalid');
      webhookUrlInput.focus();
      return;
    }

    const userId = this.getUserId();
    if (!userId) return;

    const button = event.currentTarget || event.target;
    const previousUrl = webhookUrlInput.dataset.initialValue || '';
    setButtonLoading(button, true);

    try {
      const checkbox = document.getElementById('webhook_for_all_lead_orders');
      const saveBody = {
        action_type: 'save',
        webhook_url: webhookUrl
      };
      if (checkbox) {
        saveBody.webhook_for_all_lead_orders = checkbox.checked;
      }

      const response = await patch(`/users/${userId}/manage_webhook`, {
        body: JSON.stringify(saveBody),
        contentType: 'application/json',
      });

      const data = await response.json;
      if (response.ok) {
        this.showAlert(data?.message || 'Webhook saved successfully!', 'success');
        // Update initial value so dirty state resets
        webhookUrlInput.dataset.initialValue = webhookUrl;
        // Reset checkbox initial state
        if (checkbox) {
          checkbox.dataset.initialChecked = checkbox.checked ? 'true' : 'false';
        }
        // Update button states without reloading
        this.toggleWebhookButtons();
        const integrationCard = integrationCardFor(button);
        if (webhookUrl !== previousUrl) {
          // A new URL is untested, so delivery is off until its Test passes (#6705).
          this.updateWebhookStatusBadge('not_tested');
          setIntegrationCardState(integrationCard, { connected: false });
        } else if (isIntegrationCardConnected(integrationCard)) {
          setIntegrationCardCollapsed(integrationCard, true);
        }
        this.updateWebhookActionButton('update');
      } else {
        this.showAlert(data?.error || 'Failed to save webhook', 'danger');
      }
    } catch (error) {
      this.showAlert('An error occurred while saving the webhook', 'danger');
      console.error(error);
    } finally {
      // Clear the busy state but let toggleWebhookButtons control the disabled state
      clearButtonBusy(button);
      this.toggleWebhookButtons();
    }
  }

  // The not-connected status line in the Webhook card body (users/_lead_integrations_form).
  updateWebhookStatusBadge(status) {
    const row = this.element.querySelector('.webhook-test-status');
    if (!row) return;

    const badgeSpec = WEBHOOK_STATUS_BADGES[status];
    setHidden(row, !badgeSpec);
    if (!badgeSpec) return;

    let badge = row.querySelector('.webhook-status-badge');
    if (!badge) {
      badge = document.createElement('span');
      row.appendChild(badge);
    }
    badge.className = `${statusBadgeClass(badgeSpec.tone)} webhook-status-badge`;
    badge.textContent = badgeSpec.label;
  }

  updateWebhookActionButton(mode) {
    if (!this.hasWebhookCardContainerTarget) return;
    const container = this.webhookCardContainerTarget.closest('.gl-settings-integrations-card');
    if (!container) return;
    const actionBtn = container.querySelector('.gl-profile-section-action button[data-action*="addWebhookIntegration"]');
    if (!actionBtn) return;

    if (mode === 'update') {
      actionBtn.innerHTML = '<i class="ph ph-pencil-simple me-1"></i>Update Webhook Integration';
    } else {
      actionBtn.innerHTML = '<i class="ph ph-plus me-1"></i>Add Webhook Integration';
    }
  }

  // Badge state from TestLeadsController's answer (webhook_url_valid,
  // webhook_test_status) — the only writer of the saved webhook's test state.
  webhookBadgeStatusFrom(data) {
    if (data?.webhook_url_valid === true) return 'success';
    if (data?.webhook_test_status === 'failed') return 'failed';
    return 'not_tested';
  }

  async handleDeleteWebhook(event) {
    event.preventDefault();
    // Save reference before await — event.currentTarget becomes null after async boundary
    const button = event.currentTarget;
    if (!(await showConfirmModal('Are you sure you want to delete the webhook URL?'))) {
      return;
    }

    const userId = this.getUserId();
    if (!userId) return;

    setButtonLoading(button, true);
    try {
      const response = await patch(`/users/${userId}/manage_webhook`, {
        body: JSON.stringify({ action_type: 'delete' }),
        contentType: 'application/json',
      });

      const data = await response.json;
      if (response.ok) {
        this.showAlert(data?.message || 'Webhook URL deleted successfully!', 'success');
        // Clear URL, hide the card body, remove header and badge, update button states
        const webhookUrlInput = document.getElementById('user_webhook_url');
        if (webhookUrlInput) {
          webhookUrlInput.value = '';
          webhookUrlInput.dataset.initialValue = '';
        }
        // Uncheck and reset the "for all orders" checkbox
        const checkbox = document.getElementById('webhook_for_all_lead_orders');
        if (checkbox) {
          checkbox.checked = false;
          checkbox.dataset.initialChecked = 'false';
        }
        const body = button?.closest('.webhook-card')?.querySelector('.webhook-card-body');
        if (body) setHidden(body, true);
        const statusRow = this.element.querySelector('.webhook-test-status');
        if (statusRow) setHidden(statusRow, true);
        setIntegrationCardState(integrationCardFor(button), { connected: false });
        this.toggleWebhookButtons();
        // Switch bottom button back to "Add" label
        this.updateWebhookActionButton('add');
      } else {
        this.showAlert(data?.error || 'Failed to delete webhook', 'danger');
      }
    } catch (error) {
      this.showAlert('An error occurred while deleting the webhook', 'danger');
      console.error(error);
    } finally {
      // Clear the busy state; toggleWebhookButtons owns the disabled state and its reason.
      clearButtonBusy(button);
      this.toggleWebhookButtons();
    }
  }

  addWebhookIntegration(event) {
    event.preventDefault();
    if (!this.hasWebhookCardContainerTarget) return;

    const card = this.webhookCardContainerTarget.querySelector('.webhook-card');
    if (!card) return;

    // Open the form and focus the input
    const body = card.querySelector('.webhook-card-body');
    if (body) setHidden(body, false);
    const webhookUrlInput = document.getElementById('user_webhook_url');
    if (webhookUrlInput) webhookUrlInput.focus();
  }

  cancelWebhookIntegration(event) {
    event.preventDefault();
    const card = event.currentTarget.closest('.webhook-card');
    if (!card) return;

    // A connected card closes; otherwise the form hides again behind "+ Add"
    const integrationCard = integrationCardFor(card);
    if (isIntegrationCardConnected(integrationCard)) {
      setIntegrationCardCollapsed(integrationCard, true);
    } else {
      const body = card.querySelector('.webhook-card-body');
      if (body) setHidden(body, true);
    }

    // Revert URL input to initial value (saved URL) or clear if new
    const webhookUrlInput = document.getElementById('user_webhook_url');
    if (webhookUrlInput) {
      webhookUrlInput.value = webhookUrlInput.dataset.initialValue || '';
      webhookUrlInput.classList.remove('is-invalid');
    }
    // Revert checkbox to initial state
    const checkbox = document.getElementById('webhook_for_all_lead_orders');
    if (checkbox) {
      checkbox.checked = checkbox.dataset.initialChecked === 'true';
    }
    this.toggleWebhookButtons();
  }

  async handleTestWebhook(event) {
    event.preventDefault();
    const webhookUrlInput = document.getElementById('user_webhook_url');
    const webhookUrl = webhookUrlInput?.value?.trim();

    if (!webhookUrl) {
      this.showAlert('Please save a webhook URL first', 'warning');
      return;
    }

    if (!this.isValidUrl(webhookUrl)) {
      this.showAlert('Invalid URL. Please enter a valid URL starting with http:// or https://', 'danger');
      return;
    }

    const button = event.currentTarget || event.target;
    setButtonLoading(button, true);

    try {
      const response = await post('/test_leads', {
        body: JSON.stringify({
          test_lead: {
            service: 'webhook',
            webhook_url: webhookUrl
          }
        }),
        contentType: 'application/json',
      });

      const data = await response.json;
      // The badge follows the SERVER's answer for the SAVED webhook: TestLeadsController
      // stamps webhook_url_valid / webhook_test_status only when the tested URL is the
      // persisted one and echoes them back. A 200 for any other URL leaves the saved
      // webhook exactly as it was, so painting "Successfully Tested" from the HTTP
      // status alone claimed a delivery gate the server never opened.
      this.updateWebhookStatusBadge(this.webhookBadgeStatusFrom(data));
      if (typeof data?.webhook_url_valid === 'boolean') {
        setIntegrationCardState(integrationCardFor(button), { connected: data.webhook_url_valid });
      }

      if (!response.ok) {
        this.showAlert(data?.error || 'Failed to send test lead', 'danger');
      } else if (data?.webhook_url_valid === false) {
        this.showAlert(UNSAVED_WEBHOOK_TESTED_MESSAGE, 'warning');
      } else {
        this.showAlert(data?.message || 'Test lead sent to webhook successfully!', 'success');
      }
    } catch (error) {
      // No answer from the server: the saved webhook's state is unknown, so the
      // badge stays as it was rather than asserting a failure that may not exist.
      this.showAlert('An error occurred while sending test lead', 'danger');
      console.error(error);
    } finally {
      setButtonLoading(button, false);
    }
  }

  async handleTestGhl(event) {
    event.preventDefault();
    const button = event.currentTarget;
    setButtonLoading(button, true);

    try {
      const response = await post('/test_leads', {
        body: JSON.stringify({
          test_lead: {
            service: 'ghl'
          }
        }),
        contentType: 'application/json',
      });

      const data = await response.json;
      if (response.ok) {
        this.showAlert(data?.message || 'Test lead sent to GHL successfully!', 'success');
      } else {
        this.showAlert(data?.error || 'Failed to send test lead to GHL', 'danger');
      }
    } catch (error) {
      this.showAlert('An error occurred while sending test lead to GHL', 'danger');
      console.error(error);
    } finally {
      setButtonLoading(button, false);
    }
  }

  // ─── InsurDial integration ─────────────────────────────────────────────────
  setupInsurdialButtons() {
    this.toggleInsurdialButtons();
  }

  toggleInsurdialButtons() {
    const input = document.getElementById('user_insurdial_api_key');
    const saveBtn = document.getElementById('save-insurdial-btn');
    const testBtn = document.getElementById('insurdial-test-lead');
    const deleteBtn = document.getElementById('delete-insurdial-btn');
    const checkbox = document.getElementById('insurdial_for_all_lead_orders');
    if (!input || !saveBtn) return;

    input.classList.remove('is-invalid');

    const value = input.value.trim();
    const initial = input.dataset.initialValue || '';
    const hasSavedKey = initial.length > 0;
    const hasKeyValue = value.length > 0;
    const keyDirty = value !== initial;

    let checkboxDirty = false;
    if (checkbox) {
      const initialChecked = checkbox.dataset.initialChecked === 'true';
      checkbox.disabled = !hasKeyValue;
      if (!hasKeyValue && checkbox.checked) {
        checkbox.checked = false;
      }
      checkboxDirty = checkbox.checked !== initialChecked;
    }

    const isDirty = keyDirty || checkboxDirty;

    const reason = !isDirty ? SAVE_UNCHANGED_REASON : (hasKeyValue ? null : INSURDIAL_KEY_REASON);
    this.applyGate(saveBtn, reason);
    this.applyGate(testBtn, hasKeyValue ? null : INSURDIAL_KEY_REASON);
    this.applyGate(deleteBtn, hasSavedKey ? null : INSURDIAL_NOT_SAVED_REASON);
  }

  async handleSaveInsurDial(event) {
    event.preventDefault();
    const input = document.getElementById('user_insurdial_api_key');
    if (input == null) return;

    const apiKey = input.value.trim();

    if (!apiKey) {
      await showAlertModal('Please enter an InsurDial API key.', { title: 'API key required', variant: 'danger' });
      input.classList.add('is-invalid');
      input.focus();
      return;
    }

    const button = event.currentTarget;
    if (button) setButtonLoading(button, true);

    try {
      const checkbox = document.getElementById('insurdial_for_all_lead_orders');
      const response = await patch('/settings/integrations/insurdial', {
        body: JSON.stringify({
          insurdial_api_key: apiKey,
          insurdial_for_all_lead_orders: checkbox?.checked || false,
        }),
        contentType: 'application/json',
        responseKind: 'json',
      });

      const data = await response.json;
      if (response.ok) {
        input.dataset.initialValue = apiKey;
        if (checkbox) {
          checkbox.dataset.initialChecked = checkbox.checked ? 'true' : 'false';
          checkbox.disabled = false;
        }

        setIntegrationCardState(integrationCardFor(button), { connected: true, collapsed: true });

        this.updateInsurdialActionButton('update');
        this.toggleInsurdialButtons();
        this.showAlert(data?.message || 'InsurDial API key saved.', 'success');
      } else {
        const errs = (data?.errors || [data?.error || 'Save failed.']).join(', ');
        this.showAlert(errs, 'danger');
      }
    } catch (error) {
      this.showAlert('An error occurred while saving InsurDial API key.', 'danger');
      console.error(error);
    } finally {
      if (button) setButtonLoading(button, false);
      this.toggleInsurdialButtons();
    }
  }

  updateInsurdialActionButton(mode) {
    if (!this.hasInsurdialCardContainerTarget) return;
    const container = this.insurdialCardContainerTarget.closest('.gl-settings-integrations-card');
    if (!container) return;
    const actionBtn = container.querySelector('.gl-profile-section-action button[data-action*="addInsurdialIntegration"]');
    if (!actionBtn) return;

    if (mode === 'update') {
      actionBtn.innerHTML = '<i class="ph ph-pencil-simple me-1"></i>Update InsurDial API Key';
    } else {
      actionBtn.innerHTML = '<i class="ph ph-plus me-1"></i>Add InsurDial API Key';
    }
  }

  addInsurdialIntegration(event) {
    event.preventDefault();
    if (!this.hasInsurdialCardContainerTarget) return;

    const card = this.insurdialCardContainerTarget.querySelector('.insurdial-card');
    if (!card) return;

    const body = card.querySelector('.insurdial-card-body');
    if (body) setHidden(body, false);
    const input = document.getElementById('user_insurdial_api_key');
    if (input) input.focus();
  }

  handleCancelInsurDial(event) {
    event.preventDefault();
    const card = event.currentTarget.closest('.insurdial-card');
    const input = document.getElementById('user_insurdial_api_key');
    const checkbox = document.getElementById('insurdial_for_all_lead_orders');

    if (input) {
      input.value = input.dataset.initialValue || '';
      input.classList.remove('is-invalid');
    }
    if (checkbox) {
      checkbox.checked = checkbox.dataset.initialChecked === 'true';
    }

    const hasSavedKey = (input?.dataset.initialValue || '').length > 0;
    if (card) {
      if (hasSavedKey) {
        setIntegrationCardCollapsed(integrationCardFor(card), true);
      } else {
        const body = card.querySelector('.insurdial-card-body');
        if (body) setHidden(body, true);
      }
    }

    this.toggleInsurdialButtons();
  }

  async handleDeleteInsurDial(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const confirmed = await showConfirmModal(
      'Delete InsurDial API key? Leads will stop being sent to InsurDial.',
      { title: 'Delete InsurDial API key?', confirmText: 'Delete', variant: 'danger' }
    );
    if (!confirmed) return;

    if (button) setButtonLoading(button, true);

    try {
      const response = await patch('/settings/integrations/insurdial', {
        body: JSON.stringify({
          insurdial_api_key: '',
          insurdial_for_all_lead_orders: false,
        }),
        contentType: 'application/json',
        responseKind: 'json',
      });

      const data = await response.json;
      if (response.ok) {
        const input = document.getElementById('user_insurdial_api_key');
        if (input) {
          input.value = '';
          input.dataset.initialValue = '';
          input.classList.remove('is-invalid');
        }

        const checkbox = document.getElementById('insurdial_for_all_lead_orders');
        if (checkbox) {
          checkbox.checked = false;
          checkbox.dataset.initialChecked = 'false';
          checkbox.disabled = true;
        }

        const body = button?.closest('.insurdial-card')?.querySelector('.insurdial-card-body');
        if (body) setHidden(body, true);
        setIntegrationCardState(integrationCardFor(button), { connected: false });

        this.updateInsurdialActionButton('add');
        this.toggleInsurdialButtons();
        this.showAlert(data?.message || 'InsurDial API key deleted.', 'success');
      } else {
        const errs = (data?.errors || [data?.error || 'Delete failed.']).join(', ');
        this.showAlert(errs, 'danger');
      }
    } catch (error) {
      this.showAlert('An error occurred while deleting InsurDial API key.', 'danger');
      console.error(error);
    } finally {
      if (button) setButtonLoading(button, false);
      this.toggleInsurdialButtons();
    }
  }

  async handleTestInsurDial(event) {
    event.preventDefault();
    const input = document.getElementById('user_insurdial_api_key');
    const apiKey = input?.value?.trim();
    if (!apiKey) {
      this.showAlert('Enter an API key first.', 'warning');
      return;
    }

    const button = event.currentTarget;
    if (button) setButtonLoading(button, true);

    try {
      const response = await post('/settings/integrations/insurdial/test', {
        body: JSON.stringify({ insurdial_api_key: apiKey }),
        contentType: 'application/json',
        responseKind: 'json',
      });

      const data = await response.json;
      if (response.ok && data?.success) {
        this.showAlert(`InsurDial test succeeded (HTTP ${data.status}).`, 'success');
      } else {
        const msg = data?.error || `InsurDial test failed (HTTP ${data?.status || '?'}).`;
        this.showAlert(msg, 'danger');
      }
    } catch (error) {
      this.showAlert('An error occurred while testing InsurDial.', 'danger');
      console.error(error);
    } finally {
      if (button) setButtonLoading(button, false);
    }
  }

  handleGoogleSheetFormSubmit(event) {
    // Show loading state when the Google Sheet form is submitted
    if (this.hasGoogleSheetFormContentTarget) {
      this.googleSheetFormContentTarget.classList.add('d-none');
    }
    if (this.hasGoogleSheetLoadingTarget) {
      this.googleSheetLoadingTarget.classList.remove('d-none');
    }
    if (this.hasGoogleSheetFooterTarget) {
      this.googleSheetFooterTarget.classList.add('d-none');
    }
    if (this.hasGoogleSheetModalCloseTarget) {
      this.googleSheetModalCloseTarget.classList.add('d-none');
    }
  }

  async handleGoogleSheetTestLead(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const integrationId = button.dataset.integrationId;

    if (!integrationId) {
      this.showAlert('Integration ID not found', 'danger');
      return;
    }

    setButtonLoading(button, true);

    try {
      const response = await post(`/google_sheet_integrations/${integrationId}/send_test_lead`, {
        contentType: 'application/json',
        responseKind: 'json',
      });

      const data = await response.json;
      if (response.ok) {
        this.showAlert(data?.message || 'Test lead sent successfully!', 'success');
      } else {
        this.showAlert(data?.error || 'Failed to send test lead', 'danger');
      }
    } catch (error) {
      this.showAlert('An error occurred while sending test lead', 'danger');
      console.error(error);
    } finally {
      setButtonLoading(button, false);
    }
  }

  async handleGoogleSheetDelete(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const integrationId = button.dataset.integrationId;
    const integrationName = button.dataset.integrationName || 'this integration';

    if (!integrationId) {
      this.showAlert('Integration ID not found', 'danger');
      return;
    }

    if (!(await showConfirmModal(`Are you sure you want to delete integration "${integrationName}"? Note: this does not delete the google sheet, only the linking to it.`))) {
      return;
    }

    setButtonLoading(button, true);

    try {
      const response = await del(`/google_sheet_integrations/${integrationId}`, {
        contentType: 'application/json',
        responseKind: 'json',
      });

      if (response.ok) {
        this.showAlert(`Google Sheet integration "${integrationName}" deleted.`, 'success');
        const integrationCard = integrationCardFor(button);
        const card = button.closest('.google-sheet-card');
        if (card) card.remove();
        this.collapseAllGoogleSheetCards();
        // The last sheet is gone: the card goes back to just "+ Add".
        if (!integrationCard?.querySelector('button[data-action*="handleGoogleSheetDelete"]')) {
          setIntegrationCardState(integrationCard, { connected: false });
        }
      } else {
        const data = await response.json;
        this.showAlert(data?.error || 'Failed to delete integration', 'danger');
        setButtonLoading(button, false);
      }
    } catch (error) {
      this.showAlert('An error occurred while deleting the integration', 'danger');
      console.error(error);
      setButtonLoading(button, false);
    }
  }

  async handleGoogleSheetSave(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const card = button.closest('.google-sheet-card');
    if (!card) return;

    const integrationId = button.dataset.integrationId;
    if (!integrationId) {
      this.showAlert('Integration ID not found', 'danger');
      return;
    }

    const defaultCheckbox = card.querySelector('.google-sheet-default-section input[type="checkbox"]');
    const defaultForLeadType = defaultCheckbox?.checked ? true : false;
    const leadType = defaultCheckbox?.dataset?.leadType || '';
    const orderKindSelect = card.querySelector('.google-sheet-order-kind-select');
    const orderKind = orderKindSelect?.value || 'all';
    const attributes = { default_for_lead_type: defaultForLeadType };
    if (orderKindSelect) attributes.order_kind = orderKind;
    // Export fields (#17081): sent only when changed — a change can add a tab
    // to the sheet — and never the "keep the saved fields" placeholder.
    const fieldsSelect = card.querySelector('.google-sheet-export-profile-select');
    if (fieldsSelect && fieldsSelect.value !== fieldsSelect.dataset.initialValue && fieldsSelect.value !== 'keep') {
      attributes.export_profile_id = fieldsSelect.value;
    }

    setButtonLoading(button, true);

    try {
      // responseKind is required: contentType only describes the request body, and
      // without an Accept header Rails takes the HTML branch and 302-redirects,
      // making a committed save look like a failure (#10683).
      const response = await patch(`/google_sheet_integrations/${integrationId}`, {
        body: JSON.stringify({
          google_sheet_integration: attributes
        }),
        contentType: 'application/json',
        responseKind: 'json',
      });

      const data = await response.json;
      if (response.ok) {
        this.showAlert(data?.message || 'Integration updated successfully.', 'success');
        this.updateGoogleSheetCardTitle(card, { defaultForLeadType });
        this.updateGoogleSheetOrderKindBadge(card);
        this.syncGoogleSheetDefaults(card, leadType, defaultForLeadType, orderKind);
        // Reset initial checked state so dirty tracking resets
        const savedCheckbox = card.querySelector('.google-sheet-default-section input[type="checkbox"]');
        if (savedCheckbox) savedCheckbox.dataset.initialChecked = savedCheckbox.checked ? 'true' : 'false';
        if (orderKindSelect) orderKindSelect.dataset.initialValue = orderKind;
        if (fieldsSelect) fieldsSelect.dataset.initialValue = fieldsSelect.value;
        this.collapseAllGoogleSheetCards();
        // Restore button HTML but keep it disabled (with its reason) after a successful save
        clearButtonBusy(button);
        this.applyGate(button, SAVE_UNCHANGED_REASON);
        return;
      } else {
        this.showAlert(data?.error || 'Failed to save', 'danger');
      }
    } catch (error) {
      this.showAlert('An error occurred while saving', 'danger');
      console.error(error);
    }
    setButtonLoading(button, false);
  }

  updateGoogleSheetCardTitle(card, { defaultForLeadType } = {}) {
    const titleElement = card.querySelector('.google-sheet-title');
    if (!titleElement) return;

    let defaultBadge = titleElement.querySelector('.gl-status-badge--primary');
    if (defaultForLeadType) {
      if (!defaultBadge) {
        defaultBadge = document.createElement('span');
        defaultBadge.className = `${statusBadgeClass('primary')} ms-2`;
        defaultBadge.textContent = 'Default';
        titleElement.appendChild(defaultBadge);
      }
    } else if (defaultBadge) {
      defaultBadge.remove();
    }
  }

  // The order-kind badge is hidden for "Real-time and aged" sheets and shows the
  // selected option's short label otherwise.
  updateGoogleSheetOrderKindBadge(card) {
    const badge = card.querySelector('.google-sheet-order-kind-badge');
    const select = card.querySelector('.google-sheet-order-kind-select');
    if (!badge || !select) return;

    const option = select.options[select.selectedIndex];
    badge.textContent = option?.dataset?.badge || '';
    badge.classList.toggle('d-none', select.value === 'all');
  }

  // Mirrors GoogleSheetIntegration#unset_other_defaults: one default per lead
  // type AND order kind (real-time / aged / both), so only a default of the same
  // kind is cleared.
  syncGoogleSheetDefaults(savedCard, savedLeadType, isDefault, savedOrderKind = 'all') {
    if (!this.hasGoogleSheetContainerTarget || !isDefault) return;

    this.googleSheetContainerTarget.querySelectorAll('.google-sheet-card').forEach(card => {
      if (card === savedCard) return;

      const checkbox = card.querySelector('.google-sheet-default-section input[type="checkbox"]');
      if (!checkbox?.checked) return;

      const cardOrderKind = card.querySelector('.google-sheet-order-kind-select')?.dataset?.initialValue || 'all';
      if (cardOrderKind !== savedOrderKind) return;

      const cardLeadType = checkbox.dataset?.leadType || '';

      let shouldRemove = false;
      if (savedLeadType === '') {
        shouldRemove = true;
      } else if (cardLeadType === savedLeadType || cardLeadType === '') {
        shouldRemove = true;
      }

      if (shouldRemove) {
        checkbox.checked = false;
        checkbox.dataset.initialChecked = 'false';
        this.updateGoogleSheetCardTitle(card, { defaultForLeadType: false });
        this.applyGate(card.querySelector('.google-sheet-save-btn'), SAVE_UNCHANGED_REASON);
      }
    });
  }

  async handleGoogleSheetShare(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const integrationId = button.dataset.integrationId;
    const row = button.closest('.google-sheet-share-row') || button.closest('.shared-email-row') || button.closest('tr');
    const emailInput = row?.querySelector('.share-email-input');
    const permissionSelect = row?.querySelector('.share-permission-select');

    const email = emailInput?.value?.trim();
    const permission = permissionSelect?.value || 'read';

    if (!integrationId) {
      this.showAlert('Integration ID not found', 'danger');
      return;
    }

    if (!email || !this.isValidEmail(email)) {
      await showAlertModal('Please enter a valid email address.', { title: 'Invalid Email', variant: 'danger' });
      if (emailInput) {
        emailInput.classList.add('is-invalid');
        emailInput.focus();
      }
      return;
    }

    setButtonLoading(button, true);

    try {
      const response = await post(`/google_sheet_integrations/${integrationId}/share`, {
        body: JSON.stringify({ email, permission }),
        contentType: 'application/json',
        responseKind: 'json',
      });

      const data = await response.json;
      if (response.ok) {
        this.showAlert(data?.message || 'Sheet shared successfully!', 'success');
        setTimeout(() => window.location.reload(), 1500);
      } else {
        this.showAlert(data?.error || 'Failed to share sheet', 'danger');
        setButtonLoading(button, false);
      }
    } catch (error) {
      this.showAlert('An error occurred while sharing the sheet', 'danger');
      console.error(error);
      setButtonLoading(button, false);
    }
  }

  toggleShareControls(event) {
    const input = event.currentTarget;
    const row = input.closest('.google-sheet-share-row');
    if (!row) return;

    // Clear invalid state when user edits
    input.classList.remove('is-invalid');

    const hasValidEmail = this.isValidEmail(input.value.trim());
    const permissionSelect = row.querySelector('.share-permission-select');
    const inviteBtn = row.querySelector('.share-invite-btn');

    if (permissionSelect) permissionSelect.disabled = !hasValidEmail;
    this.applyGate(inviteBtn, hasValidEmail ? null : SHARE_EMAIL_REASON);
  }

  async handleGoogleSheetUnshare(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const integrationId = button.dataset.integrationId;
    const email = button.dataset.email;

    if (!integrationId || !email) {
      this.showAlert('Missing required information', 'danger');
      return;
    }

    if (!(await showConfirmModal(`Are you sure you want to revoke access for ${email}?`))) {
      return;
    }

    setButtonLoading(button, true);

    try {
      const response = await del(`/google_sheet_integrations/${integrationId}/unshare?email=${encodeURIComponent(email)}`, {
        contentType: 'application/json',
        responseKind: 'json',
      });

      const data = await response.json;
      if (response.ok) {
        this.showAlert(data?.message || 'Access revoked successfully!', 'success');
        setTimeout(() => window.location.reload(), 1500);
      } else {
        this.showAlert(data?.error || 'Failed to revoke access', 'danger');
        setButtonLoading(button, false);
      }
    } catch (error) {
      this.showAlert('An error occurred while revoking access', 'danger');
      console.error(error);
      setButtonLoading(button, false);
    }
  }

  async handleGoogleSheetUpdatePermission(event) {
    event.preventDefault();
    const select = event.currentTarget;
    const integrationId = select.dataset.integrationId;
    const email = select.dataset.email;
    const permission = select.value;

    if (!integrationId || !email) {
      this.showAlert('Missing required information', 'danger');
      return;
    }

    select.disabled = true;

    try {
      const response = await patch(`/google_sheet_integrations/${integrationId}/update_permission`, {
        body: JSON.stringify({ email, permission }),
        contentType: 'application/json',
        responseKind: 'json',
      });

      const data = await response.json;
      if (response.ok) {
        this.showAlert(data?.message || 'Permission updated successfully!', 'success');
      } else {
        this.showAlert(data?.error || 'Failed to update permission', 'danger');
        // Revert the select to original value would require tracking original
      }
    } catch (error) {
      this.showAlert('An error occurred while updating permission', 'danger');
      console.error(error);
    } finally {
      select.disabled = false;
    }
  }

  getUserId() {
    // this.element is the form itself (controller is on the form)
    const action = this.element.action || this.element.querySelector('form')?.action;
    if (action) {
      const match = action.match(/\/users\/(\d+)/);
      return match ? match[1] : null;
    }
    return null;
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // Validates URL format - mirrors UrlFormatChecker from app/validators/url_validator.rb
  isValidUrl(urlString) {
    try {
      const url = new URL(urlString);
      // Check scheme is http or https
      if (!['http:', 'https:'].includes(url.protocol)) {
        return false;
      }
      // Check host is present and has valid format (contains a dot followed by at least 2 chars)
      if (!url.hostname || !url.hostname.match(/\..{2,}/)) {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }

  showAlert(message, type) {
    const colorClass = type === 'success' ? 'bg-success' :
                       type === 'danger' ? 'bg-danger' :
                       type === 'warning' ? 'bg-warning' : 'bg-info';

    if (window.showToast) {
      window.showToast(message, colorClass);
    } else {
      const variant = type === 'success' ? 'success'
        : type === 'danger' ? 'danger'
        : type === 'warning' ? 'warning' : 'primary';
      const title = type === 'success' ? 'Success'
        : type === 'danger' ? 'Error' : 'Notice';
      showAlertModal(message, { variant, title });
    }
  }

  selectAllStates(event) {
    event.preventDefault();

    if (!this.hasLicensedStatesTarget) return;

    const jQuery = window.jQuery || window.$;
    if (!jQuery) return;

    const $select = jQuery(this.licensedStatesTarget);
    const allOptions = Array.from(this.licensedStatesTarget.options)
      .map((option) => option.value)
      .filter((val) => this.includeInSelectAll(val));

    $select.val(allOptions).trigger("change");
    this.updateStatesCounter();
  }

  removeAllStates(event) {
    event.preventDefault();

    if (!this.hasLicensedStatesTarget) return;

    const jQuery = window.jQuery || window.$;
    if (!jQuery) return;

    jQuery(this.licensedStatesTarget).val([]).trigger("change");
    this.updateStatesCounter();
  }

  updateStatesCounter() {
    if (!this.hasStatesCounterTarget || !this.hasLicensedStatesTarget)
      return;

    const jQuery = window.jQuery || window.$;
    const rawSelectedValues = jQuery
      ? (jQuery(this.licensedStatesTarget).val() || [])
      : Array.from(this.licensedStatesTarget.selectedOptions).map((option) => option.value);
    const selectedValues = this.normalizedSelectedStateValues(rawSelectedValues);
    const count = selectedValues.filter((value) => Boolean(value)).length;

    this.statesCounterTarget.textContent = `(${count} selected)`;
    this.updateStatesSummary(selectedValues);
  }

  updateStatesSummary(selectedValues) {
    if (!this.hasStatesSummaryTarget || !this.hasLicensedStatesTarget) return;

    const selectedSet = new Set(selectedValues.filter((value) => Boolean(value)));
    const addAllValues = Array.from(this.licensedStatesTarget.options)
      .map((option) => option.value)
      .filter((value) => this.includeInSelectAll(value));
    const count = selectedSet.size;
    const allAddAllStatesSelected = addAllValues.length > 0 &&
      addAllValues.every((value) => selectedSet.has(value));

    if (allAddAllStatesSelected) {
      this.statesSummaryTarget.textContent = "Licensed in all available states";
    } else {
      const noun = count === 1 ? "state" : "states";
      this.statesSummaryTarget.textContent = `${count} ${noun} selected`;
    }
  }

  includeInSelectAll(value) {
    return Boolean(value) && !LICENSED_STATE_SELECT_ALL_EXCLUSIONS.has(value);
  }

  normalizedSelectedStateValues(values) {
    if (Array.isArray(values)) return values;
    if (typeof values === "string") return values ? [values] : [];

    return [];
  }

  async showDobInfo(event) {
    event.preventDefault();
    await showAlertModal(
      "By adding your Date of Birth you will get a discount code on your birthday from GOAT Leads!",
      { title: "Birthday Discount", variant: "info" },
    );
  }

  selectAllDays(event) {
    event.preventDefault();
    this.element
      .querySelectorAll('input[name="user[default_days_per_week][]"]')
      .forEach((checkbox) => (checkbox.checked = true));
    this.updateDaysSummary();
  }

  updateDaysSummary() {
    if (!this.hasDaysSummaryTarget) return;
    const days = this.getCheckedDays().map((checkbox) => checkbox.value);
    this.daysSummaryTarget.textContent = formatDeliveryDaysSummary(days);
  }

  clearAllDays(event) {
    event.preventDefault();

    const checkedDays = this.getCheckedDays();
    if (checkedDays.length === 0) {
      showAlertModal("You must have at least one day per week selected.", { variant: "warning", title: "Selection required" });
      return;
    }

    showAlertModal(
      "You must have at least one day per week selected. Please select the days you want before clearing.",
      { variant: "warning", title: "Selection required" },
    );
  }

  toggleSameAsHome(event) {
    const isChecked = event.target.checked;

    if (isChecked) {
      this.copyHomeToBusinessAddress();
      this.setBusinessAddressDisabled(true);
    } else {
      this.clearBusinessAddress();
      this.setBusinessAddressDisabled(false);
    }
    this.toggleBusinessAddressVisibility(isChecked);
    this.updateBusinessAddressSummary();
  }

  syncHomeAddressField(event) {
    if (!this.hasSameAsHomeTarget || !this.sameAsHomeTarget.checked) return;

    const fieldId = event.target.id;
    let fieldName = fieldId.replace("user_home_address_", "");
    const targetName = `businessAddress${this.capitalize(fieldName)}Target`;
    const businessField = this[targetName];

    if (businessField) {
      businessField.value = event.target.value;
    }
    this.updateBusinessAddressSummary();
  }

  copyHomeToBusinessAddress() {
    const fieldMappings = [
      ["street", "street"],
      ["city", "city"],
      ["state", "state"],
      ["zip", "zip"],
      ["country", "country"],
    ];

    fieldMappings.forEach(([home, business]) => {
      // Home fields are in a sibling form outside this controller scope (settings page
      // has two separate forms), so use document.querySelector. Business fields are always
      // inside this form, so use this.element.querySelector.
      const homeField = document.querySelector(
        `#user_home_address_${home}`,
      );
      const businessField = this.element.querySelector(
        `#user_business_address_${business}`,
      );

      if (homeField && businessField) {
        businessField.value = homeField.value;
      }
    });
    this.updateBusinessAddressSummary();
  }

  clearBusinessAddress() {
    ["street", "city", "state", "zip", "country"].forEach((field) => {
      const businessField = this.element.querySelector(
        `#user_business_address_${field}`,
      );
      if (businessField) {
        businessField.value = "";
      }
    });
    this.updateBusinessAddressSummary();
  }

  setBusinessAddressDisabled(disabled) {
    ["street", "city", "state", "zip", "country"].forEach((field) => {
      const businessField = this.element.querySelector(
        `#user_business_address_${field}`,
      );
      if (businessField) {
        businessField.disabled = disabled;
      }
    });
  }

  syncBusinessAddressIfNeeded() {
    if (this.hasSameAsHomeTarget && this.sameAsHomeTarget.checked) {
      this.copyHomeToBusinessAddress();
      this.setBusinessAddressDisabled(true);
      this.toggleBusinessAddressVisibility(true);
    }
  }

  toggleBusinessAddressVisibility(useSummary) {
    if (this.hasBusinessAddressGroupTarget) {
      this.businessAddressGroupTarget.classList.toggle("d-none", useSummary);
    }
    if (this.hasBusinessAddressSummaryTarget) {
      this.businessAddressSummaryTarget.classList.toggle("d-none", !useSummary);
    }
  }

  updateBusinessAddressSummary() {
    if (!this.hasBusinessAddressSummaryTextTarget) return;

    const parts = ["street", "city", "state", "zip", "country"]
      .map((field) => {
        const input = this.element.querySelector(`#user_business_address_${field}`);
        return input?.value?.trim();
      })
      .filter(Boolean);

    this.businessAddressSummaryTextTarget.textContent =
      parts.join(", ") || "Complete your home address to sync it here.";
  }

  // "Use Profile Info For Thank You Page Info" (owner rulings 2026-09-26,
  // #14488). Ticking it copies the profile in, locks the copied boxes and also
  // turns on "Use On All Orders" — orders with thank-you page data close
  // more, so the agent is pushed onto it (not recorded as their own toggle:
  // handleSubmit's asymmetric auto-check keeps its own rule). Unticking ONLY
  // unlocks the boxes: every value stays, the borrowed photo preview stays —
  // "what if the user checks the box and only wants to change 1 thing" — and
  // the thank-you box is left as it is. The one way to empty the boxes is the
  // deliberate "Clear" beside the heading (#clearDisplayedAgentInfo).
  toggleDefaultsSameAsProfile(event) {
    const isChecked = event.target.checked;

    if (isChecked) {
      this.syncDefaultsFromProfile();
      this.setDefaultsDisabled(true);
      if (this.hasUseProfileInfoForThankYouPageTarget) {
        this.useProfileInfoForThankYouPageTarget.checked = true;
      }
    } else {
      this.setDefaultsDisabled(false);
    }
  }

  syncProfileField(event) {
    if (
      !this.hasDefaultsSameAsProfileTarget ||
      !this.defaultsSameAsProfileTarget.checked
    )
      return;
    this.syncDefaultsFromProfile();
  }

  syncDefaultsFromProfile() {
    // Profile source values are passed in via Stimulus values because the
    // source fields (#user_first_name, etc.) live on a different /settings
    // tab than the display fields and are not in the DOM here.
    this.syncDisplayNameFromProfile();

    // The Display Title is the one box the copy never overwrites (#13319,
    // owner ruling 2026-09-17): it stays editable while the flag is on, and a
    // title the agent chose — a chip, or typed — is theirs until they blank it
    // or press "Use profile title". Only an EMPTY box takes the profile title.
    const displayTitle = this.element.querySelector(this.constructor.DISPLAY_TITLE_SELECTOR);
    if (displayTitle && displayTitle.value.trim() === "") {
      displayTitle.value = this.profileTitleValue || "";
    }

    const displayNpn = this.element.querySelector("#user_display_npn");
    if (displayNpn) {
      displayNpn.value = this.profileNpnValue || "";
    }

    const displayEmail = this.element.querySelector("#user_display_email");
    if (displayEmail) {
      displayEmail.value = this.profileEmailValue || "";
    }

    const displayPhone = this.element.querySelector("#user_display_phone");
    if (displayPhone) {
      displayPhone.value = this.profilePhoneValue || "";
    }

    const displayAgentWebsite = this.element.querySelector("#user_website");
    if (displayAgentWebsite) {
      displayAgentWebsite.value = this.profileWebsiteValue || "";
    }

    this.borrowProfilePhotoPreview();
    this.syncTitleSuggestions();
    this.syncTitleSource();
    this.refreshChecklist();
  }

  // Display Name is the full name leads see. While it is still blank, the
  // legal full name, or this controller's earlier suggestion, keep it in step
  // with the preferred first name. A different value is the agent's custom
  // display name and is left untouched.
  suggestDisplayNameFromPreferredFirstName() {
    if (!this.hasPreferredFirstNameTarget) return;

    const displayName = this.element.querySelector("#user_display_name");
    if (!displayName) return;

    const current = displayName.value.trim();
    if (!this.mayReplaceDisplayName(current)) return;

    const suggested = this.displayNameFor(this.preferredFirstNameTarget.value);
    this.suggestedDisplayName = suggested;
    this.writeAsTyped(displayName, suggested);
  }

  syncDisplayNameFromProfile() {
    const displayName = this.element.querySelector("#user_display_name");
    if (!displayName || !this.mayReplaceDisplayName(displayName.value.trim())) return;

    displayName.value = this.displayNameFor(this.preferredFirstNameForDisplay());
  }

  mayReplaceDisplayName(current) {
    return current === "" ||
      current === this.legalDisplayName() ||
      current === this.displayNameFor(this.profilePreferredFirstNameValue || "") ||
      current === this.displayNameFor(this.preferredFirstNameForDisplay()) ||
      current === this.suggestedDisplayName;
  }

  legalDisplayName() {
    return this.displayNameFor("");
  }

  displayNameFor(preferredFirstName) {
    return [preferredFirstName.trim() || this.profileFirstNameValue, this.profileLastNameValue]
      .filter(Boolean)
      .join(" ");
  }

  preferredFirstNameForDisplay() {
    return this.hasPreferredFirstNameTarget
      ? this.preferredFirstNameTarget.value
      : this.profilePreferredFirstNameValue;
  }

  // "Use Profile Info For Thank You Page Info" may BORROW the profile photo
  // into the Display Photo box (owner ruling 4, #12911): the preview shows the
  // profile photo the server will attach as display_photo on save. A fill,
  // never a link — an existing Display Photo, or a file the agent picked, wins
  // and the preview is left alone. The blob itself is copied server-side
  // (Users::FillDisplayPhotoFromProfile), so nothing here touches the input.
  // The hidden borrow flag mirrors the preview (#14488): "1" while it shows,
  // so a borrow that outlives an untick of the box is still made on save.
  borrowProfilePhotoPreview() {
    if (!this.hasDisplayPhotoPreviewTarget || !this.profilePhotoUrlValue) return;
    const box = this.displayPhotoPreviewTarget;
    if (box.dataset.displayPhotoAttached === "true") return;
    if (this.displayPhotoInput()?.files?.length) return;

    if (this.displayPhotoPlaceholderHtml === undefined) {
      this.displayPhotoPlaceholderHtml = box.innerHTML;
    }
    box.innerHTML = "";
    const img = document.createElement("img");
    img.src = this.profilePhotoUrlValue;
    img.alt = "Your profile photo, to be saved as your Display Photo";
    // Same sizing the slot's image-preview applies to a chosen file.
    const wiring = box.closest('[data-controller~="image-preview"]');
    const imgStyle = wiring?.dataset.imagePreviewImgStyleValue;
    const imgClass = wiring?.dataset.imagePreviewImgClassValue;
    if (imgClass) img.className = imgClass;
    else if (imgStyle) img.setAttribute("style", imgStyle);
    box.appendChild(img);
    this.displayPhotoBorrowed = true;
    // Remembered past a chosen-then-cancelled file: the borrow is what the
    // agent had, whatever the box says now (an untick keeps everything, #14488).
    this.displayPhotoBorrowWanted = true;
    this.setDisplayPhotoBorrowFlag(true);
    this.toggleDisplayPhotoBorrowNote(true);
    this.refreshChecklist();
  }

  restoreDisplayPhotoPreview() {
    if (!this.displayPhotoBorrowed || !this.hasDisplayPhotoPreviewTarget) return;
    this.displayPhotoPreviewTarget.innerHTML = this.displayPhotoPlaceholderHtml ?? "";
    this.displayPhotoBorrowed = false;
    this.displayPhotoBorrowWanted = false;
    this.setDisplayPhotoBorrowFlag(false);
    this.toggleDisplayPhotoBorrowNote(false);
    this.refreshChecklist();
  }

  setDisplayPhotoBorrowFlag(borrowed) {
    if (!this.hasDisplayPhotoBorrowFlagTarget) return;
    this.displayPhotoBorrowFlagTarget.value = borrowed ? "1" : "0";
  }

  // change-> on the Display Photo input (after photo-crop and image-preview
  // have run): a chosen file replaces the borrowed preview; a cancelled crop
  // (photo-crop clears the input and re-fires change) offers the borrow again
  // while the checkbox is ticked OR while the borrow was what the agent had
  // before they opened the file picker — an untick keeps the borrowed preview
  // (#14488), so the cancelled crop must hand it back the same way. Only the
  // Clear control (restoreDisplayPhotoPreview) forgets it.
  displayPhotoChosen(event) {
    const hasFile = (event.target.files?.length ?? 0) > 0;
    if (hasFile) {
      this.displayPhotoBorrowed = false;
      this.setDisplayPhotoBorrowFlag(false);
      this.toggleDisplayPhotoBorrowNote(false);
      this.refreshChecklist();
      return;
    }
    const boxTicked = this.hasDefaultsSameAsProfileTarget && this.defaultsSameAsProfileTarget.checked;
    if (boxTicked || this.displayPhotoBorrowWanted) {
      this.borrowProfilePhotoPreview();
    }
    this.refreshChecklist();
  }

  displayPhotoInput() {
    return this.element.querySelector(this.constructor.DISPLAY_PHOTO_INPUT_SELECTOR);
  }

  toggleDisplayPhotoBorrowNote(show) {
    if (!this.hasDisplayPhotoBorrowNoteTarget) return;
    setHidden(this.displayPhotoBorrowNoteTarget, !show);
  }

  // The "Clear" control beside the "Displayed Agent Info" heading (owner ruling
  // 2026-09-26, #14488): the ONE deliberate way to empty the display boxes.
  // While the profile copy is on the boxes are locked, so it is unticked (and
  // the boxes unlocked) first; then EVERY box — the Display Title and the
  // calendar link included — is emptied as if typed, so the unsaved-changes guard and the card's dirty
  // state hear it, and the borrowed photo preview goes back to the placeholder.
  clearDisplayedAgentInfo(event) {
    event?.preventDefault();
    if (this.hasDefaultsSameAsProfileTarget && this.defaultsSameAsProfileTarget.checked) {
      this.defaultsSameAsProfileTarget.checked = false;
      this.setDefaultsDisabled(false);
    }
    this.clearDefaults();
  }

  static DISPLAY_FIELD_SELECTORS = [
    "#user_display_name",
    "#user_display_title",
    "#user_display_npn",
    "#user_display_email",
    "#user_display_phone",
    "#user_website",
    "#user_display_calendar_link",
  ];

  clearDefaults() {
    this.constructor.DISPLAY_FIELD_SELECTORS.forEach((selector) => {
      const field = this.element.querySelector(selector);
      if (field && field.value !== "") this.writeAsTyped(field, "");
    });
    this.restoreDisplayPhotoPreview();
    this.syncTitleSuggestions();
    this.syncTitleSource();
    this.refreshChecklist();
  }

  // Suggested titles (#12908): the chips above #user_display_title live under
  // their own controller (title-suggestions) and re-read the input on
  // `title-suggestions:sync`. Every programmatic write here — the profile copy,
  // the clear, the disable — fires none of the input events the chips listen
  // for, so tell them after each one (pressed chip, weak hint, locked line).
  syncTitleSuggestions() {
    document
      .querySelectorAll('[data-controller~="title-suggestions"]')
      .forEach((el) => el.dispatchEvent(new CustomEvent("title-suggestions:sync")));
  }

  trackThankYouInteraction() {
    // The user explicitly toggled the thank-you-page checkbox; from now
    // on, handleSubmit's auto-check logic leaves it alone.
    this.thankYouUserInteracted = true;
  }

  syncDefaultsIfNeeded() {
    if (
      this.hasDefaultsSameAsProfileTarget &&
      this.defaultsSameAsProfileTarget.checked
    ) {
      this.syncDefaultsFromProfile();
      this.setDefaultsDisabled(true);
    }
  }

  // The Display Title is NOT in this list (#13319): it stays editable, and its
  // chips stay live, while the profile copy is on.
  setDefaultsDisabled(disabled) {
    const fields = [
      "#user_display_name",
      "#user_display_npn",
      "#user_display_email",
      "#user_display_phone",
      "#display_country_code",
      "#user_website",
    ];
    fields.forEach((selector) => {
      const field = this.element.querySelector(selector);
      if (field) {
        field.disabled = disabled;
      }
    });
    this.syncTitleSuggestions();
    this.refreshChecklist();
  }

  // ─── Display Title source (#13319) ──────────────────────────────────────
  // The line under the Title box says where the title on screen comes from —
  // the profile, or the agent's own — and offers "Use profile title" only while
  // it is the agent's own and there is a profile title to go back to. The
  // server (ProfileDisplayDefaults#display_title_custom?) applies the same
  // test on save: equal to the profile title (trimmed, squished, any case) =
  // following; anything else present = custom and kept.
  static DISPLAY_TITLE_SELECTOR = "#user_display_title";
  static TITLE_SOURCE_COPY = {
    following: "Following your profile title.",
    custom: "Custom title — kept even when your profile title changes.",
  };

  syncTitleSource() {
    if (!this.hasDisplayTitleSourceTarget) return;
    const input = this.element.querySelector(this.constructor.DISPLAY_TITLE_SELECTOR);
    const shown = this.normalizeTitle(input?.value);
    const profile = this.normalizeTitle(this.profileTitleValue);
    const following = shown.length > 0 && shown === profile;
    const custom = shown.length > 0 && !following;

    setHidden(this.displayTitleSourceTarget, shown.length === 0);
    this.displayTitleSourceTarget.dataset.titleSource = custom ? "custom" : "profile";
    if (this.hasDisplayTitleSourceTextTarget) {
      this.displayTitleSourceTextTarget.textContent =
        this.constructor.TITLE_SOURCE_COPY[custom ? "custom" : "following"];
    }
    if (this.hasUseProfileTitleTarget) {
      setHidden(this.useProfileTitleTarget, !(custom && profile.length > 0));
    }
  }

  // "Use profile title": put the profile title back in the box and let every
  // listener run as for typing (chips, unsaved-changes, the checklist). Saving
  // then hands the title back to the profile (ProfileDisplayDefaults).
  useProfileTitle(event) {
    event?.preventDefault();
    const input = this.element.querySelector(this.constructor.DISPLAY_TITLE_SELECTOR);
    if (!input) return;
    this.writeAsTyped(input, this.profileTitleValue || "");
    this.syncTitleSource();
    input.focus();
  }

  // "Use my GOAT calendar" (#13319 owner addition 3): put the published GOAT
  // booking link back in the Display Calendar Link box. The URL rides on the
  // button (Users::CalendarLinkPrefill#goat_link); saving persists it like any
  // typed link and ends a remembered clear. Nothing here publishes a calendar.
  useGoatCalendarLink(event) {
    event?.preventDefault();
    const input = this.element.querySelector("#user_display_calendar_link");
    const url = event?.currentTarget?.dataset?.goatCalendarUrl || "";
    if (!input || !url) return;
    this.writeAsTyped(input, url);
    input.focus();
  }

  writeAsTyped(input, value) {
    input.value = value;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // Mirrors RealtimeOrders::ThankYouTitleSuggestions.normalize (downcase + squish).
  normalizeTitle(value) {
    return String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
  }

  // The checklist (lead-profile-checklist) listens for this on the form: every
  // write above that fires no input event tells it to re-read the boxes.
  refreshChecklist() {
    this.element.dispatchEvent(new CustomEvent("lead-profile-checklist:refresh"));
  }

  toggleEmailNotifications(event) {
    if (this.hasEmailNotificationIntervalTarget) {
      if (event.target.checked) {
        this.emailNotificationIntervalTarget.classList.remove("d-none");
      } else {
        this.emailNotificationIntervalTarget.classList.add("d-none");
      }
    }
  }

  toggleSmsNotifications(event) {
    if (this.hasSmsNotificationIntervalTarget) {
      if (event.target.checked) {
        this.smsNotificationIntervalTarget.classList.remove("d-none");
      } else {
        this.smsNotificationIntervalTarget.classList.add("d-none");
      }
    }
  }

  toggleNotificationIntervals() {
    if (
      this.hasEmailNotificationsTarget &&
      this.hasEmailNotificationIntervalTarget
    ) {
      if (this.emailNotificationsTarget.checked) {
        this.emailNotificationIntervalTarget.classList.remove("d-none");
      } else {
        this.emailNotificationIntervalTarget.classList.add("d-none");
      }
    }

    if (
      this.hasSmsNotificationsTarget &&
      this.hasSmsNotificationIntervalTarget
    ) {
      if (this.smsNotificationsTarget.checked) {
        this.smsNotificationIntervalTarget.classList.remove("d-none");
      } else {
        this.smsNotificationIntervalTarget.classList.add("d-none");
      }
    }
  }

  toggleRingyCard(event) {
    const card = event.currentTarget.closest('.ringy-integration-card');
    if (card) card.classList.toggle('collapsed');
  }

  toggleGoogleSheetCard(event) {
    const card = event.currentTarget.closest('.google-sheet-card');
    if (card) card.classList.toggle('collapsed');
  }

  collapseAllGoogleSheetCards() {
    if (!this.hasGoogleSheetContainerTarget) return;
    this.googleSheetContainerTarget
      .querySelectorAll('.google-sheet-card')
      .forEach(c => c.classList.add('collapsed'));
  }

  syncRingyDefaults(savedCard, savedLeadType, isDefault) {
    if (!this.hasRingyContainerTarget || !isDefault) return;

    this.ringyContainerTarget.querySelectorAll('.ringy-integration-card').forEach(card => {
      if (card === savedCard) return;

      const checkbox = card.querySelector('input[type="checkbox"][name*="[default_for_lead_type]"]');
      if (!checkbox?.checked) return;

      const cardLeadType = card.querySelector('select[name*="[lead_type]"]')?.value || '';

      let shouldRemove = false;
      if (savedLeadType === '') {
        // "All" default → remove all other defaults
        shouldRemove = true;
      } else if (cardLeadType === savedLeadType || cardLeadType === '') {
        // Specific lead type → remove same lead type AND "All" defaults
        shouldRemove = true;
      }

      if (shouldRemove) {
        checkbox.checked = false;
        if (checkbox.dataset.initialChecked !== undefined) {
          checkbox.dataset.initialChecked = 'false';
        }
        const cardName = card.querySelector('input[name*="[name]"]')?.value || 'Ringy Integration';
        this.updateRingyCardTitle(card, cardName, { leadType: cardLeadType, defaultForLeadType: false });
        // Reset save button dirty state for this card
        this.syncRingySave(card);
      }
    });
  }

  // The lead type's display name as the server rendered it (owner ruling 51):
  // the card's own Lead Type option text, from LeadTypeRegistry.humanize_type —
  // "Spanish Lead" for NewSpanishLead, never a name rebuilt from the class.
  ringyLeadTypeLabel(card, leadType) {
    const select = card.querySelector('select[name*="[lead_type]"]');
    const option = Array.from(select?.options || []).find((candidate) => candidate.value === leadType);
    return option?.textContent.trim() || leadType;
  }

  updateRingyCardTitle(card, name, { leadType, defaultForLeadType } = {}) {
    const titleElement = card.querySelector('.ringy-card-title');
    if (!titleElement) return;

    const chevron = titleElement.querySelector('.ringy-card-toggle');
    titleElement.textContent = name;
    if (chevron) titleElement.prepend(chevron);

    // Update lead type badge
    let leadTypeBadge = titleElement.querySelector('.gl-status-badge--lead-type');
    if (leadType) {
      if (!leadTypeBadge) {
        leadTypeBadge = document.createElement('span');
        leadTypeBadge.className = `${statusBadgeClass('lead-type')} ms-2`;
        titleElement.appendChild(leadTypeBadge);
      }
      leadTypeBadge.textContent = this.ringyLeadTypeLabel(card, leadType);
    } else if (leadTypeBadge) {
      leadTypeBadge.remove();
    }

    // Update default badge
    let defaultBadge = titleElement.querySelector('.gl-status-badge--primary');
    if (defaultForLeadType) {
      if (!defaultBadge) {
        defaultBadge = document.createElement('span');
        defaultBadge.className = `${statusBadgeClass('primary')} ms-2`;
        defaultBadge.textContent = 'Default';
        titleElement.appendChild(defaultBadge);
      }
    } else if (defaultBadge) {
      defaultBadge.remove();
    }
  }

  updateRingyDefaultLabel(event) {
    const select = event.currentTarget;
    const card = select.closest('.ringy-integration-card');
    if (!card) return;

    const label = card.querySelector('.ringy-default-lead-type-label');
    if (!label) return;

    const selectedOption = select.options[select.selectedIndex];
    label.textContent = selectedOption?.text || 'All';
  }

  trackRingyDirtyState(event) {
    const card = event.currentTarget.closest('.ringy-integration-card');
    if (!card) return;

    this.syncRingySave(card);
  }

  // One dirty rule for saved and new cards: a new card's fields carry blank
  // initial values (the template stamps them), so it stays disabled until
  // something is entered (BUTTON-9).
  syncRingySave(card) {
    const saveBtn = card.querySelector('.ringy-save-btn');
    if (!saveBtn) return;
    this.applyGate(saveBtn, this.isRingyCardDirty(card) ? null : SAVE_UNCHANGED_REASON);
  }

  isRingyCardDirty(card) {
    const inputs = card.querySelectorAll('input[data-initial-value], select[data-initial-value]');
    for (const input of inputs) {
      if (input.value.trim() !== (input.dataset.initialValue || '')) return true;
    }

    const checkbox = card.querySelector('input[type="checkbox"][data-initial-checked]');
    if (checkbox) {
      const initialChecked = checkbox.dataset.initialChecked === 'true';
      if (checkbox.checked !== initialChecked) return true;
    }

    return false;
  }

  resetRingyInitialValues(card) {
    card.querySelectorAll('input[data-initial-value], select[data-initial-value]').forEach(input => {
      input.dataset.initialValue = input.value.trim();
    });
    const checkbox = card.querySelector('input[type="checkbox"][data-initial-checked]');
    if (checkbox) {
      checkbox.dataset.initialChecked = checkbox.checked ? 'true' : 'false';
    }
    this.syncRingySave(card);
  }

  trackGoogleSheetDirtyState(event) {
    const card = event.currentTarget.closest('.google-sheet-card');
    if (!card) return;
    this.syncGoogleSheetSave(card);
  }

  syncGoogleSheetSave(card) {
    const saveBtn = card.querySelector('.google-sheet-save-btn');
    if (!saveBtn) return;
    this.applyGate(saveBtn, this.isGoogleSheetCardDirty(card) ? null : SAVE_UNCHANGED_REASON);
  }

  isGoogleSheetCardDirty(card) {
    const checkbox = card.querySelector('input[type="checkbox"]');
    const orderKindSelect = card.querySelector('.google-sheet-order-kind-select');
    const defaultChanged = !!checkbox && checkbox.checked !== (checkbox.dataset.initialChecked === 'true');
    const orderKindChanged = !!orderKindSelect && orderKindSelect.value !== orderKindSelect.dataset.initialValue;
    const fieldsSelect = card.querySelector('.google-sheet-export-profile-select');
    const fieldsChanged = !!fieldsSelect && fieldsSelect.value !== fieldsSelect.dataset.initialValue;
    return defaultChanged || orderKindChanged || fieldsChanged;
  }

  addRingyIntegration(event) {
    event.preventDefault();

    if (!this.hasRingyTemplateTarget || !this.hasRingyContainerTarget) return;

    const uniqueId = Date.now().toString();
    const html = this.ringyTemplateTarget.innerHTML.replace(
      /NEW_RECORD/g,
      uniqueId,
    );
    this.ringyContainerTarget.insertAdjacentHTML("beforeend", html);
    const card = this.ringyContainerTarget.lastElementChild;
    if (card) this.syncRingySave(card);
  }

  addGoogleSheetIntegration(event) {
    event.preventDefault();

    if (!this.hasGoogleSheetTemplateTarget || !this.hasGoogleSheetContainerTarget) return;

    const uniqueId = Date.now().toString();
    const html = this.googleSheetTemplateTarget.innerHTML.replace(
      /NEW_RECORD/g,
      uniqueId,
    );
    this.googleSheetContainerTarget.insertAdjacentHTML("beforeend", html);
  }

  cancelGoogleSheetCard(event) {
    event.preventDefault();
    const card = event.currentTarget.closest('.google-sheet-card');
    if (card) card.remove();
  }

  async handleGoogleSheetCardCreate(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const card = button.closest('.google-sheet-card');
    if (!card) return;

    const name = card.querySelector('[name*="[name]"]')?.value?.trim();
    const leadType = card.querySelector('[name*="[lead_type]"]')?.value?.trim();
    const defaultCheckbox = card.querySelector('[name*="[default_for_lead_type]"]');
    const defaultForLeadType = defaultCheckbox?.checked || false;
    const orderKind = card.querySelector('[name*="[order_kind]"]')?.value || 'all';
    const exportProfileSelect = card.querySelector('[name*="[export_profile_id]"]');
    const userId = button.dataset.userId;

    if (!name || !leadType) {
      this.showAlert('Name and Lead Type are required', 'danger');
      return;
    }

    setButtonLoading(button, true);

    const attributes = {
      name, lead_type: leadType, default_for_lead_type: defaultForLeadType, order_kind: orderKind, user_id: userId
    };
    if (exportProfileSelect?.value) attributes.export_profile_id = exportProfileSelect.value;

    try {
      const response = await post('/google_sheet_integrations', {
        body: JSON.stringify({ google_sheet_integration: attributes }),
        contentType: 'application/json',
        responseKind: 'json',
      });

      const data = await response.json;
      if (response.ok) {
        this.showAlert(data?.message || 'Google Sheet created successfully!', 'success');
        // Reload the page to show the new integration with full controls
        window.location.reload();
      } else {
        this.showAlert(data?.error || 'Failed to create Google Sheet integration', 'danger');
        setButtonLoading(button, false);
      }
    } catch (error) {
      this.showAlert('An error occurred while creating the integration', 'danger');
      console.error(error);
      setButtonLoading(button, false);
    }
  }

  handleRingyCancel(event) {
    event.preventDefault();
    const card = event.currentTarget.closest('.ringy-integration-card');
    if (card) card.remove();
    // Collapse all remaining ringy cards
    this.ringyContainerTarget.querySelectorAll('.ringy-integration-card').forEach(c => {
      c.classList.add('collapsed');
    });
  }

  async handleRingyDelete(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const card = button.closest(".ringy-integration-card");
    if (!card) return;

    const integrationId = button.dataset.integrationId;
    const integrationName = button.dataset.integrationName || 'this integration';

    if (!(await showConfirmModal(`Are you sure you want to delete "${integrationName}"?`))) {
      return;
    }

    if (integrationId) {
      // Persisted record - delete via API
      setButtonLoading(button, true);

      try {
        const response = await del(`/user/ringy_integrations/${integrationId}`, {
          contentType: 'application/json',
          responseKind: 'json',
        });

        const data = await response.json;
        if (response.ok) {
          this.showAlert(data?.message || 'Ringy integration deleted.', 'success');
          const integrationCard = integrationCardFor(card);
          card.remove();
          // The last saved profile is gone: the card goes back to just "+ Add".
          if (!integrationCard?.querySelector('button[data-action*="handleRingyDelete"][data-integration-id]')) {
            setIntegrationCardState(integrationCard, { connected: false });
          }
        } else {
          this.showAlert(data?.error || 'Failed to delete', 'danger');
          setButtonLoading(button, false);
        }
      } catch (error) {
        this.showAlert('An error occurred while deleting', 'danger');
        console.error(error);
        setButtonLoading(button, false);
      }
    } else {
      // New unsaved record - just remove from DOM
      card.remove();
      this.showAlert('Ringy integration removed.', 'success');
    }
  }

  async handleRingySave(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const card = button.closest(".ringy-integration-card");
    if (!card) return;

    const integrationId = button.dataset.integrationId;
    const userId = button.dataset.userId;
    const nameInput = card.querySelector('input[name*="[name]"]');
    const sidInput = card.querySelector('input[name*="[sid]"]');
    const authTokenInput = card.querySelector('input[name*="[auth_token]"]');
    const leadTypeSelect = card.querySelector('select[name*="[lead_type]"]');
    const defaultCheckbox = card.querySelector('input[type="checkbox"][name*="[default_for_lead_type]"]');

    const name = nameInput?.value?.trim();
    const sid = sidInput?.value?.trim();
    const authToken = authTokenInput?.value?.trim();
    const leadType = leadTypeSelect?.value || '';
    const defaultForLeadType = defaultCheckbox?.checked ? true : false;

    if (!name || !sid || !authToken) {
      this.showAlert('Please fill in all required fields', 'warning');
      return;
    }

    if (!leadType) {
      this.showAlert('Please select a lead type', 'warning');
      leadTypeSelect?.focus();
      return;
    }

    if (/\s/.test(sid) || /\s/.test(authToken)) {
      this.showAlert('SID and Auth Token cannot contain spaces', 'warning');
      return;
    }

    setButtonLoading(button, true);

    try {
      const url = integrationId
        ? `/user/ringy_integrations/${integrationId}`
        : '/user/ringy_integrations';
      const requestFn = integrationId ? patch : post;

      const response = await requestFn(url, {
        body: JSON.stringify({
          ringy_integration: { name, sid, auth_token: authToken, lead_type: leadType, default_for_lead_type: defaultForLeadType, user_id: userId }
        }),
        contentType: 'application/json',
        responseKind: 'json',
      });

      const data = await response.json;
      if (response.ok) {
        this.showAlert(data?.message || 'Ringy integration saved!', 'success');
        // If new record, update card with returned ID
        if (!integrationId && data?.id) {
          button.dataset.integrationId = data.id;
          // Also update other buttons in the card
          card.querySelectorAll('button[data-action*="handleRingy"]').forEach(btn => {
            btn.dataset.integrationId = data.id;
          });
          // Update delete button name for confirmation dialog
          const deleteBtn = card.querySelector('button[data-action*="handleRingyDelete"]');
          if (deleteBtn) {
            deleteBtn.dataset.integrationName = name;
          }
          // Add Test button for new saved integration
          const actionsContainer = card.querySelector('.ringy-card-actions');
          if (actionsContainer && !card.querySelector('button[data-action*="handleRingyTestLead"]')) {
            const testButton = document.createElement('button');
            testButton.className = 'gl-btn gl-btn-outline gl-btn-xs';
            testButton.type = 'button';
            testButton.dataset.action = 'click->user-edit#handleRingyTestLead';
            testButton.dataset.sid = sid;
            testButton.dataset.authToken = authToken;
            testButton.innerHTML = '<i class="ph ph-play-circle me-1"></i>Test';
            // Insert test button between delete and save
            const saveBtn = actionsContainer.querySelector('button[data-action*="handleRingySave"]');
            actionsContainer.insertBefore(testButton, saveBtn);
          }
          // Update card title (preserve chevron icon)
          this.updateRingyCardTitle(card, name, { leadType: leadType, defaultForLeadType: defaultForLeadType });
        } else if (integrationId) {
          // For existing integration, update test button credentials
          const testBtn = card.querySelector('button[data-action*="handleRingyTestLead"]');
          if (testBtn) {
            testBtn.dataset.sid = sid;
            testBtn.dataset.authToken = authToken;
          }
          // Update card title and delete button name (preserve chevron icon)
          this.updateRingyCardTitle(card, name, { leadType: leadType, defaultForLeadType: defaultForLeadType });
          const deleteBtn = card.querySelector('button[data-action*="handleRingyDelete"]');
          if (deleteBtn) {
            deleteBtn.dataset.integrationName = name;
          }
        }
        // Update initial values so dirty state resets
        this.resetRingyInitialValues(card);
        // Sync default badges on other cards (server already handled DB uniqueness)
        this.syncRingyDefaults(card, leadType, defaultForLeadType);
        // A first saved profile connects the card (green pill, collapsed).
        setIntegrationCardState(integrationCardFor(card), { connected: true });
        // Auto-collapse all ringy cards after successful save
        this.ringyContainerTarget.querySelectorAll('.ringy-integration-card').forEach(c => {
          c.classList.add('collapsed');
        });
        // Restore button HTML but keep it disabled (with its reason) after a successful save
        clearButtonBusy(button);
        this.syncRingySave(card);
        return;
      } else {
        this.showAlert(data.errors?.join(', ') || 'Failed to save', 'danger');
      }
    } catch (error) {
      this.showAlert('An error occurred while saving', 'danger');
      console.error(error);
    }
    setButtonLoading(button, false);

  }

  async handleRingyTestLead(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const sid = button.dataset.sid;
    const authToken = button.dataset.authToken;

    if (!sid || !authToken) {
      this.showAlert('Please save the integration first before testing', 'warning');
      return;
    }

    setButtonLoading(button, true);

    try {
      const response = await post('/test_leads', {
        body: JSON.stringify({
          test_lead: {
            service: 'ringy',
            sid: sid,
            auth_token: authToken
          }
        }),
        contentType: 'application/json',
        responseKind: 'json',
      });

      const data = await response.json;
      if (response.ok) {
        this.showAlert(data?.message || 'Test lead sent to Ringy successfully!', 'success');
      } else {
        this.showAlert(data?.error || 'Failed to send test lead to Ringy', 'danger');
      }
    } catch (error) {
      this.showAlert('An error occurred while sending test lead to Ringy', 'danger');
      console.error(error);
    } finally {
      setButtonLoading(button, false);
    }
  }

  validateCredentialField(event) {
    const input = event.currentTarget;
    const hasSpaces = /\s/.test(input.value);

    if (hasSpaces) {
      input.classList.add("is-invalid");
      this.ensureInvalidFeedback(input);
    } else {
      input.classList.remove("is-invalid");
      this.removeInvalidFeedback(input);
    }
  }

  // Filter input to only allow digits (for NPN ID and Display NPN fields)
  filterDigitsOnly(event) {
    const input = event.currentTarget;
    const originalValue = input.value;
    const filteredValue = originalValue.replace(/\D/g, "");

    if (originalValue !== filteredValue) {
      input.value = filteredValue;
    }
  }

  handleSubmit(event) {
    if (this.hasSameAsHomeTarget && this.sameAsHomeTarget.checked) {
      this.setBusinessAddressDisabled(false);
    }

    // Validate credentials FIRST, before any user-visible DOM mutations
    // below (display-field sync, thank-you auto-check, daily-cap clamp).
    // If credentials are invalid we abort the submit, and the user
    // shouldn't see their other inputs silently change in the process.
    const credentialFields = this.element.querySelectorAll(
      'input[name*="[sid]"], input[name*="[auth_token]"]',
    );
    const invalidFields = Array.from(credentialFields).filter((input) =>
      /\s/.test(input.value),
    );

    if (invalidFields.length > 0) {
      event.preventDefault();
      showAlertModal(
        "SID and Auth Token fields cannot contain spaces. Please remove all spaces before saving.",
        { variant: "warning", title: "Invalid characters" },
      );
      invalidFields[0].focus();
      return;
    }

    if (
      this.hasDefaultsSameAsProfileTarget &&
      this.defaultsSameAsProfileTarget.checked
    ) {
      this.syncDefaultsFromProfile();
      this.setDefaultsDisabled(false);
    }

    // Asymmetric auto-check for "Use profile info for thank-you page":
    // if every display field the thank-you page renders is populated AND
    // the user has not explicitly toggled the checkbox during this session,
    // opt them in. If they unchecked it deliberately, respect that.
    const thankYouCheckbox = this.element.querySelector(
      "#use_profile_info_for_thank_you_page",
    );
    if (
      thankYouCheckbox &&
      !thankYouCheckbox.checked &&
      !this.thankYouUserInteracted &&
      this.allThankYouDisplayFieldsFilled()
    ) {
      thankYouCheckbox.checked = true;
    }

    // Daily lead cap: model requires nil or >= 5. Clamp sub-5 entries to
    // the minimum of 5 — preserves the user's intent (they wanted a low
    // cap) rather than nulling it out (which would mean "no cap" and
    // invert their intent).
    let dailyLeadCapWasClamped = false;
    const dailyLeadCap = this.element.querySelector("#user_daily_lead_cap");
    if (dailyLeadCap && dailyLeadCap.value.trim() !== "") {
      const numericValue = Number(dailyLeadCap.value);
      if (Number.isFinite(numericValue) && numericValue < 5) {
        dailyLeadCap.value = "5";
        dailyLeadCapWasClamped = true;
      }
    }

    this.element
      .querySelectorAll('input[name*="[_destroy]"]')
      .forEach((input) => {
        if (input.value === "1") {
          input.disabled = false;
        }
      });

    if (dailyLeadCapWasClamped) {
      // Hold the submit, surface the adjustment, then continue once the
      // user dismisses the modal. requestSubmit() (rather than submit())
      // re-runs HTML5 validation and re-fires the submit event — on the
      // second pass the value is already 5, so the clamp branch is
      // skipped and the form posts normally.
      event.preventDefault();
      const form = this.element;
      showAlertModal(
        "Your entered Daily Lead Cap was below the minimum and has been raised to 5.",
        { title: "Daily Cap Adjusted", variant: "info" },
      ).then(() => form.requestSubmit());
    }
  }

  ensureInvalidFeedback(input) {
    if (input.nextElementSibling?.classList.contains("invalid-feedback")) {
      return;
    }

    const feedback = document.createElement("div");
    feedback.className = "invalid-feedback";
    feedback.textContent = "This field cannot contain spaces";
    input.insertAdjacentElement("afterend", feedback);
  }

  removeInvalidFeedback(input) {
    if (input.nextElementSibling?.classList.contains("invalid-feedback")) {
      input.nextElementSibling.remove();
    }
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  setupFormValidation() {
    const form = this.element;
    if (!form) return;

    const originalHandleSubmit = this.handleSubmit.bind(this);
    this.handleSubmit = (event) => {
      if (!this.validateDaysPerWeek()) {
        event.preventDefault();
        showAlertModal(
          "You must select at least one day per week for default days.",
          { variant: "warning", title: "Selection required" },
        );
        return;
      }
      const windowError = this.defaultDeliveryWindowError();
      if (windowError) {
        event.preventDefault();
        this.showDefaultDeliveryWindowError(windowError);
        return;
      }
      originalHandleSubmit(event);
    };

    const dayCheckboxes = this.element.querySelectorAll(
      'input[name="user[default_days_per_week][]"]',
    );
    dayCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", (event) => {
        const checkedDays = this.getCheckedDays();
        if (checkedDays.length === 0) {
          event.preventDefault();
          checkbox.checked = true;
          showAlertModal("You must have at least one day per week selected.", { variant: "warning", title: "Selection required" });
        }
        this.updateDaysSummary();
      });
    });
  }

  // #15304: the Schedule card's Default Time of Day, held to the order's own
  // rule (lib/delivery_window ⇄ LeadOrders::DeliveryWindowRule). null when the
  // form has no such inputs or the pair is valid.
  defaultDeliveryWindowError() {
    const start = this.element.querySelector("#user_default_delivery_window_start");
    const end = this.element.querySelector("#user_default_delivery_window_end");
    if (!start || !end) return null;
    return deliveryWindowError(timeInputValue(start), timeInputValue(end));
  }

  showDefaultDeliveryWindowError(message) {
    this.defaultDeliveryWindowInputs().forEach((input) => input.classList.add("is-invalid"));
    const feedback = this.element.querySelector("#user_default_delivery_window_error");
    if (feedback) {
      feedback.textContent = message;
      feedback.classList.add("d-block");
    }
    this.defaultDeliveryWindowInputs()[0]?.focus();
  }

  clearDefaultDeliveryWindowError() {
    this.defaultDeliveryWindowInputs().forEach((input) => input.classList.remove("is-invalid"));
    const feedback = this.element.querySelector("#user_default_delivery_window_error");
    if (feedback) {
      feedback.textContent = "";
      feedback.classList.remove("d-block");
    }
  }

  defaultDeliveryWindowInputs() {
    return ["#user_default_delivery_window_start", "#user_default_delivery_window_end"]
      .map((selector) => this.element.querySelector(selector))
      .filter(Boolean);
  }

  validateDaysPerWeek() {
    const checkedDays = this.getCheckedDays();
    const dayCheckboxes = this.element.querySelectorAll(
      'input[name="user[default_days_per_week][]"]',
    );
    if (dayCheckboxes.length === 0) return true;

    return checkedDays.length > 0;
  }

  getCheckedDays() {
    const checkboxes = this.element.querySelectorAll(
      'input[name="user[default_days_per_week][]"]:checked',
    );
    return Array.from(checkboxes);
  }

  // Disconnect GHL integration via button. A DELETE through request.js (CSRF
  // token attached) — never a GET navigation, which is what /crm_cancel was.
  async handleGhlDisconnect(event) {
    event.preventDefault();

    const confirmed = await showConfirmModal(
      'Are you sure you want to disconnect Go High Level integration? This will stop syncing leads to your GHL account.'
    );
    if (!confirmed) return;

    const failure = () =>
      showAlertModal('Could not disconnect the Go High Level integration. Please try again.', {
        variant: 'danger',
        title: 'Disconnect failed',
      });

    try {
      const response = await del('/crm_cancel', { responseKind: 'json' });
      if (!response.ok) {
        failure();
        return;
      }
      const data = await response.json;
      // The server names the screen to land on; the flash it set renders there.
      this.navigateTo(data?.redirect_url || '/settings/integrations');
    } catch (error) {
      console.error(error);
      failure();
    }
  }

  // Full navigation (not Turbo.visit): the integrations screen re-renders its
  // GHL card from server state. Split out so tests observe it without jsdom's
  // non-configurable window.location.
  navigateTo(url) {
    window.location.href = url;
  }

  async handleGhlDefaultToggle(event) {
    const checkbox = event.target;
    if (!checkbox) return;

    const priorChecked = checkbox.dataset.initialChecked === 'true';
    const nextChecked = checkbox.checked;
    const userId = this.getUserId();
    if (!userId) {
      // Broken DOM (form action missing). Revert visually so the user's apparent
      // toggle matches the server state, and log a diagnostic so developers can
      // catch it — a user-facing toast here wouldn't be actionable ("fix the form").
      console.warn('handleGhlDefaultToggle: getUserId() returned null; checkbox reverted without request');
      checkbox.checked = priorChecked;
      return;
    }

    // Disable the checkbox while the request is in flight so rapid double-toggles
    // can't race and leave dataset.initialChecked drifted from the server's state.
    checkbox.disabled = true;
    try {
      const response = await patch(`/users/${userId}/manage_ghl_settings`, {
        body: JSON.stringify({ ghl_for_all_lead_orders: nextChecked }),
        contentType: 'application/json',
      });

      const data = await response.json;
      if (response.ok) {
        checkbox.dataset.initialChecked = nextChecked ? 'true' : 'false';
        this.showAlert(data?.message || 'GHL default updated', 'success');
        return;
      }

      // Non-2xx: server responded with a reason. Prefer its message over the generic fallback.
      checkbox.checked = priorChecked;
      this.showAlert(data?.error || 'Could not update GHL default', 'danger');
    } catch (error) {
      // Network / parse failure: no server message available. Different copy from the
      // non-2xx path so logs and telemetry can distinguish the two failure modes.
      console.error('handleGhlDefaultToggle failed', error);
      checkbox.checked = priorChecked;
      this.showAlert('Could not update GHL default — please try again', 'danger');
    } finally {
      checkbox.disabled = false;
    }
  }

  // Mirror of handleGhlDefaultToggle: persist the "send to Dial Tracker for all
  // orders" preference instantly via AJAX, with optimistic revert on failure.
  async handleJwDefaultToggle(event) {
    const checkbox = event.target;
    if (!checkbox) return;

    const priorChecked = checkbox.dataset.initialChecked === 'true';
    const nextChecked = checkbox.checked;
    const userId = this.getUserId();
    if (!userId) {
      // Broken DOM (form action missing). Revert visually so the user's apparent
      // toggle matches the server state, and log a diagnostic.
      console.warn('handleJwDefaultToggle: getUserId() returned null; checkbox reverted without request');
      checkbox.checked = priorChecked;
      return;
    }

    // Disable while in flight so rapid double-toggles can't race and leave
    // dataset.initialChecked drifted from the server's state.
    checkbox.disabled = true;
    try {
      const response = await patch(`/users/${userId}/manage_jw_settings`, {
        body: JSON.stringify({ jw_tracker_for_all_lead_orders: nextChecked }),
        contentType: 'application/json',
      });

      const data = await response.json;
      if (response.ok) {
        checkbox.dataset.initialChecked = nextChecked ? 'true' : 'false';
        this.showAlert(data?.message || 'Dial Tracker default updated', 'success');
        return;
      }

      checkbox.checked = priorChecked;
      this.showAlert(data?.error || 'Could not update Dial Tracker default', 'danger');
    } catch (error) {
      console.error('handleJwDefaultToggle failed', error);
      checkbox.checked = priorChecked;
      this.showAlert('Could not update Dial Tracker default — please try again', 'danger');
    } finally {
      checkbox.disabled = false;
    }
  }

  // The agent confirms they moved off JW's free plan (#15633): resume the
  // paused Dial Tracker sync, then reload so the card shows its new state.
  async handleJwPlanUpgraded(event) {
    const checkbox = event.target;
    if (!checkbox?.checked) return;

    const confirmed = await showConfirmModal(
      "Turn Dial Tracker sync back on? Only do this after upgrading your JW Tracker account to a paid plan — on the free plan, sync will pause again.",
      { confirmText: 'Turn sync back on' }
    );
    if (!confirmed) {
      checkbox.checked = false;
      return;
    }

    checkbox.disabled = true;
    try {
      const response = await patch(checkbox.dataset.resumeUrl, { contentType: 'application/json' });
      const data = await response.json;
      if (response.ok) {
        this.showAlert(data?.message || 'Dial Tracker sync is back on', 'success');
        this.reloadPage();
        return;
      }

      checkbox.checked = false;
      this.showAlert(data?.error || 'Could not turn Dial Tracker sync back on', 'danger');
    } catch (error) {
      console.error('handleJwPlanUpgraded failed', error);
      checkbox.checked = false;
      this.showAlert('Could not turn Dial Tracker sync back on — please try again', 'danger');
    } finally {
      checkbox.disabled = false;
    }
  }

  // Seam so specs can observe the reload (jsdom's location.reload is not mockable).
  reloadPage() {
    window.location.reload();
  }

  // Stub: the JW Tracker lead-push/sync isn't built yet, so there's nothing to
  // exercise — real test wiring ships with the JW push feature.
  handleTestJwTracker(event) {
    event.preventDefault();
    this.showAlert(
      "Dial Tracker sync isn't active yet — your preference is saved for when it goes live.",
      'info'
    );
  }

}
